// EMAReport.jsx
// EMA Crossover and V Pattern report
// Two tabs: Crossovers | V Patterns
// Charts button creates temp watchlist and opens in new tab with V pattern markers

import React, { useState, useEffect, useRef } from 'react';
import {
  fetchCrossoverFilters,
  fetchCrossovers,
  fetchVPatterns,
  createTempWatchlist,
  startCrossoverCalculation,
  startVPatternCalculation,
  fetchVPatternDefaults,
  saveVPatternDefaults,
  fetchVPatternFilters,
} from '../api';
import axios from 'axios';
import API_BASE from '../config';
import { api } from '../api';
import { fetchAllWatchlists, fetchWatchlistStocks } from '../api';

// ============================================================
// DATE HELPERS
// ============================================================

// Format timestamp from DB to display string
// DB stores in UTC, display in IST
function fmtTime(ts) {
  if (!ts) return '';
  // Parse and convert to IST
  const d   = new Date(ts);
  const ist = new Date(d.getTime() + 5.5 * 60 * 60 * 1000);
  const dd  = String(ist.getUTCDate()).padStart(2, '0');
  const mm  = String(ist.getUTCMonth() + 1).padStart(2, '0');
  const yy  = ist.getUTCFullYear();
  const hh  = String(ist.getUTCHours()).padStart(2, '0');
  const min = String(ist.getUTCMinutes()).padStart(2, '0');
  return `${dd}-${mm}-${yy} ${hh}:${min}`;
}

function getDateRange(window) {
  const today = new Date();
  const fmt   = (d) => d.toISOString().slice(0, 10);
  const toDate = fmt(today);

  switch (window) {
    case 'today':    return { fromDate: toDate, toDate };
    case '3days': {
      const d = new Date(today); d.setDate(d.getDate() - 3);
      return { fromDate: fmt(d), toDate };
    }
    case '1week': {
      const d = new Date(today); d.setDate(d.getDate() - 7);
      return { fromDate: fmt(d), toDate };
    }
    case '1month': {
      const d = new Date(today); d.setMonth(d.getMonth() - 1);
      return { fromDate: fmt(d), toDate };
    }
    default: return { fromDate: '', toDate: '' };
  }
}

// ============================================================
// CROSSOVERS TAB
// ============================================================
function CrossoversTab({ filters, filterOptions }) {
  const [rows,    setRows]    = useState([]);
  const [loading, setLoading] = useState(false);

  const [localFilters, setLocalFilters] = useState({
    symbol: '', timeframe: '', cross_type: '', direction: '',
    window: '1week', fromDate: '', toDate: '',
  });

  useEffect(() => {
    const { fromDate, toDate } = getDateRange(localFilters.window);
    loadData(fromDate, toDate);
  }, [localFilters]);

  const loadData = async (fromDate, toDate) => {
    setLoading(true);
    try {
      const params = {};
      if (localFilters.symbol)     params.symbol     = localFilters.symbol;
      if (localFilters.timeframe)  params.timeframe  = localFilters.timeframe;
      if (localFilters.cross_type) params.cross_type = localFilters.cross_type;
      if (localFilters.direction)  params.direction  = localFilters.direction;
      const fd = localFilters.window === 'custom' ? localFilters.fromDate : fromDate;
      const td = localFilters.window === 'custom' ? localFilters.toDate   : toDate;
      if (fd) params.from_date = fd;
      if (td) params.to_date   = td + 'T23:59:59';
      const data = await fetchCrossovers(params);
      setRows(data || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const uniqueSymbols = [...new Set(rows.map(r => r.symbol))];

  const handleChartsOpen = async () => {
    if (uniqueSymbols.length === 0) return;
    try {
      const res  = await createTempWatchlist(uniqueSymbols);
      const first = rows[rows.length - 1]; // oldest = first chronologically
      const date  = first?.cross_time?.slice(0, 10) || '';
      const url = `${window.location.origin}?watchlist=${res.watchlist_id}&jumpDate=${date}`;
      window.open(url, '_blank');
    } catch (e) { alert('Failed to create watchlist'); }
  };

  return (
    <div>
      {/* Filters */}
      <div style={styles.filterRow}>
        <select style={styles.sel} value={localFilters.symbol}
          onChange={e => setLocalFilters({...localFilters, symbol: e.target.value})}>
          <option value="">All Stocks</option>
          {filterOptions.symbols?.map(s => <option key={s} value={s}>{s}</option>)}
        </select>

        <select style={styles.sel} value={localFilters.timeframe}
          onChange={e => setLocalFilters({...localFilters, timeframe: e.target.value})}>
          <option value="">All Timeframes</option>
          {['2min','5min','15min','1H','1D','1W'].map(t =>
            <option key={t} value={t}>{t}</option>)}
        </select>

        <select style={styles.sel} value={localFilters.cross_type}
          onChange={e => setLocalFilters({...localFilters, cross_type: e.target.value})}>
          <option value="">All Pairs</option>
          {filterOptions.cross_pairs?.map(p => (
            <option key={p} value={p}>{p}</option>
          ))}
        </select>

        <select style={styles.sel} value={localFilters.direction}
          onChange={e => setLocalFilters({...localFilters, direction: e.target.value})}>
          <option value="">All Directions</option>
          <option value="up">Up ↑</option>
          <option value="down">Down ↓</option>
        </select>

        {/* Date window */}
        <div style={styles.windowRow}>
          {['today','3days','1week','1month','custom'].map(w => (
            <button key={w}
              style={{...styles.winBtn,
                ...(localFilters.window === w ? styles.winBtnActive : {})}}
              onClick={() => setLocalFilters({...localFilters, window: w})}>
              {w === 'today' ? 'Today'
                : w === '3days' ? '3 Days'
                : w === '1week' ? '1 Week'
                : w === '1month' ? '1 Month'
                : 'Custom'}
            </button>
          ))}
        </div>

        {localFilters.window === 'custom' && (
          <>
            <input type="date" style={styles.dateInput}
              value={localFilters.fromDate}
              onChange={e => setLocalFilters({...localFilters, fromDate: e.target.value})} />
            <input type="date" style={styles.dateInput}
              value={localFilters.toDate}
              onChange={e => setLocalFilters({...localFilters, toDate: e.target.value})} />
          </>
        )}

        <span style={styles.count}>{loading ? '...' : `${rows.length} rows`}</span>

        <button style={styles.chartsBtn} onClick={handleChartsOpen}
          disabled={uniqueSymbols.length === 0}>
          📊 Charts ({uniqueSymbols.length} stocks)
        </button>
      </div>

      {/* Table */}
      <div style={styles.tableWrap}>
        <table style={styles.table}>
          <thead>
            <tr>
              {['Date/Time','Symbol','Timeframe','Cross Pair','Direction','Price'].map(h => (
                <th key={h} style={styles.th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map(row => (
              <tr key={row.id} style={styles.tr}>
                <td style={styles.td}>{fmtTime(row.cross_time)}</td>
                <td style={{...styles.td, fontWeight:'bold'}}>{row.symbol}</td>
                <td style={styles.td}>{row.timeframe}</td>
                <td style={styles.td}>{row.cross_pair}</td>
                <td style={{...styles.td,
                  color: row.direction === 'up' ? '#26a69a' : '#ef5350',
                  fontWeight: 'bold'}}>
                  {row.direction === 'up' ? '↑ Up' : '↓ Down'}
                </td>
                <td style={styles.td}>{row.candle_close}</td>
              </tr>
            ))}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={8} style={styles.empty}>No crossovers found</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ============================================================
// V PATTERNS TAB
// ============================================================
function VPatternsTab({ filterOptions }) {
  const [rows,        setRows]        = useState([]);
  const [loading,     setLoading]     = useState(false);
  const [watchlists,  setWatchlists]  = useState([]);
  const [saveDefault, setSaveDefault] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(true);
  const [vpFilterOptions, setVpFilterOptions] = useState({ symbols: [], timeframes: [], emas: [] });

  const [localFilters, setLocalFilters] = useState({
    watchlist:      '',
    symbol:         '',
    timeframe:      '',
    ema:            '',
    direction:      '',
    max_gap:        '',
    min_inter_gap:  '',
    trend_confirmed:'any',
    close_rising:   'any',
    ema_rising:     'any',
    confirm_break:  '',
    window:         '1week',
    fromDate:       '',
    toDate:         '',
  });

  useEffect(() => {
    fetchAllWatchlists()
      .then(data => setWatchlists(data || []))
      .catch(console.error);
    
      fetchVPatternFilters()                           // ← add this
      .then(data => setVpFilterOptions(data || {}))  // ← add this
      .catch(console.error);

    fetchVPatternDefaults()
      .then(defaults => {
        if (defaults && Object.keys(defaults).length > 0)
          setLocalFilters(prev => ({ ...prev, ...defaults }));
      })
      .catch(console.error);
  }, []);

  useEffect(() => {
    const { fromDate, toDate } = getDateRange(localFilters.window);
    loadData(fromDate, toDate);
  }, [localFilters]);

  const boolParam = (v) => v === 'yes' ? true : v === 'no' ? false : undefined;

  const loadData = async (fromDate, toDate) => {
    setLoading(true);
    try {
      const params = {};
      if (localFilters.symbol)    params.symbol    = localFilters.symbol;
      if (localFilters.timeframe) params.timeframe = localFilters.timeframe;
      if (localFilters.ema)       params.ema       = localFilters.ema;
      if (localFilters.direction) params.direction = localFilters.direction;
      if (localFilters.max_gap !== '')
        params.max_gap = Number(localFilters.max_gap);
      if (localFilters.min_inter_gap !== '')
        params.min_inter_gap = Number(localFilters.min_inter_gap);
      const tv = boolParam(localFilters.trend_confirmed);
      const cv = boolParam(localFilters.close_rising);
      const ev = boolParam(localFilters.ema_rising);
      if (tv !== undefined) params.trend_confirmed = tv;
      if (cv !== undefined) params.close_rising    = cv;
      if (ev !== undefined) params.ema_rising      = ev;
      if (localFilters.confirm_break) params.confirm_break = localFilters.confirm_break;

      if (localFilters.watchlist && !localFilters.symbol) {
        try {
          const wlData = await fetchWatchlistStocks(localFilters.watchlist);
          const wlSymbols = (wlData || []).map(s => s.symbol);
          if (wlSymbols.length > 0) params.symbols = wlSymbols.join(',');
        } catch (_) {}
      }

      const fd = localFilters.window === 'custom'    ? localFilters.fromDate
               : localFilters.window === 'beginning' ? ''
               : fromDate;
      const td = localFilters.window === 'custom'    ? localFilters.toDate
               : localFilters.window === 'beginning' ? ''
               : toDate;
      if (fd) params.from_date = fd;
      if (td) params.to_date   = td + 'T23:59:59';

      const data = await fetchVPatterns(params);
      setRows(data || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const setFilter = (key, val) => {
    setLocalFilters(prev => {
      if (key === 'watchlist' && val) return { ...prev, watchlist: val, symbol: '' };
      if (key === 'symbol'    && val) return { ...prev, symbol: val, watchlist: '' };
      return { ...prev, [key]: val };
    });
  };

  const handleSaveDefault = async () => {
    try {
      await saveVPatternDefaults(localFilters);
      alert('Defaults saved');
      setSaveDefault(false);
    } catch (e) { alert('Failed to save defaults'); }
  };

  const canOpenCharts  = localFilters.timeframe && localFilters.ema && rows.length > 0;
  const uniqueSymbols  = [...new Set(rows.map(r => r.symbol))];

  const handleChartsOpen = async () => {
    if (!canOpenCharts) return;
    try {
      const symbols = uniqueSymbols.length > 0 ? uniqueSymbols : [];
      if (symbols.length === 0) return;
      const res = await createTempWatchlist(symbols);
      let vpFromDate = '', vpToDate = '';
      if (localFilters.window === 'custom') {
        vpFromDate = localFilters.fromDate;
        vpToDate   = localFilters.toDate;
      } else if (localFilters.window !== 'beginning') {
        const { fromDate, toDate } = getDateRange(localFilters.window);
        vpFromDate = fromDate;
        vpToDate   = toDate;
      }
      // Save markers config to sessionStorage — ChartNew reads on load
      // Group report rows by symbol — pass actual data, no re-fetch needed
      const markersBySymbol = {};
      rows.forEach(row => {
        if (!markersBySymbol[row.symbol]) markersBySymbol[row.symbol] = [];
        markersBySymbol[row.symbol].push(row);
      });

      sessionStorage.setItem('markersConfig', JSON.stringify({
        enabled:         true,
        fetchFn:         null,
        filters:         { confirm_break: localFilters.confirm_break || null },
        markersBySymbol,
      }));

      const urlParams = new URLSearchParams({
        watchlist:     res.watchlist_id,
        showVPatterns: '1',
        vpTimeframe:   localFilters.timeframe,
        vpEma:         localFilters.ema,
      });
      if (localFilters.confirm_break) urlParams.set('vpConfirmBreak', localFilters.confirm_break);
      
      window.open(`${window.location.origin}?${urlParams.toString()}`, '_blank');
    } catch (e) { alert('Failed to create watchlist'); }
  };

  const boolIcon = (val) => val === true ? '✓' : val === false ? '✗' : '—';

  // Filter summary for collapsed bar
  const filterSummary = () => {
    const parts = [];
    if (localFilters.ema)       parts.push(localFilters.ema.replace('ema_','EMA'));
    if (localFilters.timeframe) parts.push(localFilters.timeframe);
    if (localFilters.direction) parts.push(localFilters.direction.toUpperCase());
    if (localFilters.max_gap !== '')       parts.push(`Gap≤${localFilters.max_gap}`);
    if (localFilters.min_inter_gap !== '') parts.push(`InterGap≥${localFilters.min_inter_gap}`);
    if (localFilters.trend_confirmed !== 'any') parts.push(`Trend:${localFilters.trend_confirmed}`);
    if (localFilters.close_rising   !== 'any') parts.push(`Close:${localFilters.close_rising}`);
    if (localFilters.ema_rising     !== 'any') parts.push(`EMA:${localFilters.ema_rising}`);
    return parts.length > 0 ? parts.join(' | ') : 'All patterns';
  };

  const WINDOWS = ['today','3days','1week','1month','beginning','custom'];

  return (
    <div>
      {/* ── Collapsible filter panel ── */}
      <div style={styles.filterBlock}>

        {/* Filter header — always visible */}
        <div style={styles.filterHeader}
          onClick={() => setFiltersOpen(v => !v)}>
          <span style={{ fontWeight: 'bold', fontSize: '12px', color: '#444' }}>
            {filtersOpen ? '▲ Filters' : '▼ Filters'}
          </span>
          {!filtersOpen && (
            <span style={{ fontSize: '11px', color: '#666', marginLeft: '12px' }}>
              {filterSummary()}
            </span>
          )}
          <span style={{ marginLeft: 'auto', fontSize: '12px', color: '#888' }}>
            {loading ? '...' : `${rows.length} rows`}
          </span>
        </div>

        {/* Collapsible filter body */}
        {filtersOpen && (
          <div style={styles.filterBody}>
            <div style={styles.filterGrid}>

              {/* Watchlist */}
              <div style={styles.filterGroup}>
                <span style={styles.filterLabel}>Watchlist</span>
                <select style={styles.sel} value={localFilters.watchlist}
                  onChange={e => setFilter('watchlist', e.target.value)}>
                  <option value="">All Watchlists</option>
                  {watchlists.map(w => (
                    <option key={w.id} value={w.id}>{w.name}</option>
                  ))}
                </select>
              </div>

              {/* Symbol */}
              <div style={styles.filterGroup}>
                <span style={styles.filterLabel}>Symbol</span>
                <select style={styles.sel} value={localFilters.symbol}
                  onChange={e => setFilter('symbol', e.target.value)}>
                  <option value="">All Stocks</option>
                  {(vpFilterOptions.symbols || []).map(s => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>

              {/* Timeframe */}
              <div style={styles.filterGroup}>
                <span style={styles.filterLabel}>Timeframe</span>
                <select style={styles.sel} value={localFilters.timeframe}
                  onChange={e => setFilter('timeframe', e.target.value)}>
                  <option value="">Timeframe *</option>
                  {(vpFilterOptions.timeframes || []).map(t => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>

              {/* EMA */}
              <div style={styles.filterGroup}>
                <span style={styles.filterLabel}>EMA</span>
                <select style={styles.sel} value={localFilters.ema}
                  onChange={e => setFilter('ema', e.target.value)}>
                  <option value="">EMA *</option>
                  {(vpFilterOptions.emas || []).map(e => (
                    <option key={e} value={e}>{e.replace('ema_','EMA ')}</option>
                  ))}
                </select>
              </div>

              {/* Direction */}
              <div style={styles.filterGroup}>
                <span style={styles.filterLabel}>Direction</span>
                <select style={styles.sel} value={localFilters.direction}
                  onChange={e => setFilter('direction', e.target.value)}>
                  <option value="">All</option>
                  <option value="buy">Buy</option>
                  <option value="sell">Sell</option>
                </select>
              </div>

              {/* Max Gap */}
              <div style={styles.filterGroup}>
                <span style={styles.filterLabel}>Max Gap</span>
                <input style={styles.numInput} type="number" min="0" max="50"
                  placeholder="Any"
                  value={localFilters.max_gap}
                  onChange={e => setFilter('max_gap', e.target.value)} />
              </div>

              {/* Min Inter-pattern Gap */}
              <div style={styles.filterGroup}>
                <span style={styles.filterLabel}>Min Inter-pattern Gap</span>
                <input style={styles.numInput} type="number" min="0" max="500"
                  placeholder="Any"
                  value={localFilters.min_inter_gap}
                  onChange={e => setFilter('min_inter_gap', e.target.value)} />
              </div>

              {/* Trend Confirmed */}
              <div style={styles.filterGroup}>
                <span style={styles.filterLabel}>Vertex Trending</span>
                <select style={styles.sel} value={localFilters.trend_confirmed}
                  onChange={e => setFilter('trend_confirmed', e.target.value)}>
                  <option value="any">Any</option>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              </div>

              {/* Close Rising */}
              <div style={styles.filterGroup}>
                <span style={styles.filterLabel}>Close Trending</span>
                <select style={styles.sel} value={localFilters.close_rising}
                  onChange={e => setFilter('close_rising', e.target.value)}>
                  <option value="any">Any</option>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              </div>

              {/* EMA Rising */}
              <div style={styles.filterGroup}>
                <span style={styles.filterLabel}>EMA Trending</span>
                <select style={styles.sel} value={localFilters.ema_rising}
                  onChange={e => setFilter('ema_rising', e.target.value)}>
                  <option value="any">Any</option>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              </div>

              {/* Confirm Break */}
              <div style={styles.filterGroup}>
                <span style={styles.filterLabel}>Confirm Break</span>
                <select style={styles.sel} value={localFilters.confirm_break}
                  onChange={e => setFilter('confirm_break', e.target.value)}>
                  <option value="">Any</option>
                  <option value="close">Close basis</option>
                  <option value="hl">High/Low basis</option>
                </select>
              </div>

            </div>

            {/* Date window row */}
            <div style={{ display: 'flex', alignItems: 'center',
                          gap: '6px', marginTop: '10px', flexWrap: 'wrap' }}>
              <span style={styles.filterLabel}>Period:</span>
              <div style={styles.windowRow}>
                {WINDOWS.map(w => (
                  <button key={w}
                    style={{...styles.winBtn,
                      ...(localFilters.window === w ? styles.winBtnActive : {})}}
                    onClick={() => setLocalFilters({...localFilters, window: w})}>
                    {w === 'today'     ? 'Today'
                      : w === '3days'    ? '3 Days'
                      : w === '1week'    ? '1 Week'
                      : w === '1month'   ? '1 Month'
                      : w === 'beginning'? 'Since Beginning'
                      : 'Custom'}
                  </button>
                ))}
              </div>
              {localFilters.window === 'custom' && (
                <>
                  <input type="date" style={styles.dateInput}
                    value={localFilters.fromDate}
                    onChange={e => setLocalFilters({...localFilters, fromDate: e.target.value})} />
                  <input type="date" style={styles.dateInput}
                    value={localFilters.toDate}
                    onChange={e => setLocalFilters({...localFilters, toDate: e.target.value})} />
                </>
              )}

              {/* Charts button */}
              <button
                style={{...styles.chartsBtn,
                  opacity: canOpenCharts ? 1 : 0.4,
                  cursor:  canOpenCharts ? 'pointer' : 'not-allowed',
                  marginLeft: 'auto',
                }}
                onClick={handleChartsOpen}
                disabled={!canOpenCharts}
                title={!localFilters.timeframe || !localFilters.ema
                  ? 'Select Timeframe and EMA first' : ''}>
                📊 Charts ({uniqueSymbols.length} stocks)
              </button>

              {/* Save default */}
              <label style={{ display: 'flex', alignItems: 'center', gap: '4px',
                              fontSize: '12px', color: '#555', cursor: 'pointer' }}>
                <input type="checkbox" checked={saveDefault}
                  onChange={e => setSaveDefault(e.target.checked)} />
                Set as Default
              </label>
              {saveDefault && (
                <button onClick={handleSaveDefault} style={styles.calcBtn}>Save</button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Table */}
      <div style={styles.tableWrap}>
        <table style={styles.table}>
          <thead>
            <tr>
              {['Date/Time','Symbol','TF','EMA','Dir','Gap',
                'Vertex','Prev Vertex','Vertex Trend','Close Trend','EMA Trend','CB-C','CB-HL','Price'].map(h => (
                <th key={h} style={styles.th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map(row => (
              <tr key={row.id} style={styles.tr}>
                <td style={styles.td}>{fmtTime(row.pattern_time)}</td>
                <td style={{...styles.td, fontWeight:'bold'}}>{row.symbol}</td>
                <td style={styles.td}>{row.timeframe}</td>
                <td style={styles.td}>{row.ema?.replace('ema_','EMA')}</td>
                <td style={{...styles.td, fontWeight:'bold',
                  color: row.direction === 'buy' ? '#26a69a' : '#ef5350'}}>
                  {row.direction === 'buy' ? '▲ Buy' : '▼ Sell'}
                </td>
                <td style={{...styles.td, textAlign:'center'}}>
                  {row.gap_candles}
                </td>
                <td style={styles.td}>
                  {row.vertex_price?.toFixed(2)}
                </td>
                <td style={styles.td}>
                  {row.prev_vertex?.toFixed(2) || '—'}
                </td>
                <td style={{...styles.td, textAlign:'center',
                  color: row.trend_confirmed === true  ? '#26a69a'
                       : row.trend_confirmed === false ? '#ef5350' : '#999'}}>
                  {boolIcon(row.trend_confirmed)}
                </td>
                <td style={{...styles.td, textAlign:'center',
                  color: row.candle_close != null && row.prev_candle_close != null
                    ? ((row.direction === 'buy'
                        ? row.candle_close > row.prev_candle_close
                        : row.candle_close < row.prev_candle_close) ? '#26a69a' : '#ef5350')
                    : '#999'}}>
                  {row.candle_close != null && row.prev_candle_close != null
                    ? ((row.direction === 'buy'
                        ? row.candle_close > row.prev_candle_close
                        : row.candle_close < row.prev_candle_close) ? '✓' : '✗')
                    : '—'}
                </td>
                <td style={{...styles.td, textAlign:'center',
                  color: row.ema_value != null && row.prev_ema_value != null
                    ? ((row.direction === 'buy'
                        ? row.ema_value > row.prev_ema_value
                        : row.ema_value < row.prev_ema_value) ? '#26a69a' : '#ef5350')
                    : '#999'}}>
                  {row.ema_value != null && row.prev_ema_value != null
                    ? ((row.direction === 'buy'
                        ? row.ema_value > row.prev_ema_value
                        : row.ema_value < row.prev_ema_value) ? '✓' : '✗')
                    : '—'}
                </td>
                <td style={styles.td}>
                  {row.confirm_break_close === true ? '✓'
                    : row.confirm_break_close === false ? '✗' : '—'}
                </td>
                <td style={styles.td}>
                  {row.confirm_break_hl === true ? '✓'
                    : row.confirm_break_hl === false ? '✗' : '—'}
                </td>
                <td style={styles.td}>{row.candle_close?.toFixed(2)}</td>
              </tr>
            ))}
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={14} style={styles.empty}>No V patterns found</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ============================================================
// CALCULATE SECTION
// ============================================================
function CalculateSection() {
  const [msg,        setMsg]        = useState('');
  const [running,    setRunning]    = useState(false);
  const [runType,    setRunType]    = useState('');  // 'crossovers' or 'vpatterns'
  const [watchlists, setWatchlists] = useState([]);
  const [watchlist,  setWatchlist]  = useState('');
  const [symbol,     setSymbol]     = useState('');
  const pollRef = useRef(null);

  useEffect(() => {
    fetchAllWatchlists()
      .then(data => setWatchlists(data || []))
      .catch(console.error);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  const startPolling = (type) => {
    if (pollRef.current) clearInterval(pollRef.current);
    const url = type === 'crossovers'
      ? `/crossovers/calculate/status`
      : `/v_patterns/calculate/status`;

    let errCount = 0;

    const poll = async () => {
      try {
        const res  = await api.get(url);
        errCount   = 0;                       // reset on any success
        const data = res.data;

        if (data.last_lines && data.last_lines.length > 0) {
          setMsg(data.last_lines[data.last_lines.length - 1]);
        }
        if (!data.running) {
          clearInterval(pollRef.current);
          setRunning(false);
          if (data.ok === false) {
            const why = data.note || `exit code ${data.exit_code}`;
            setMsg(`❌ Failed — ${why} (see log)`);
          } else {
            setMsg('✅ Completed successfully');
          }
        }
      } catch (e) {
        errCount += 1;                        // transient error — keep polling
        if (errCount >= 5) {                  // ~10s of continuous failure
          clearInterval(pollRef.current);
          setRunning(false);
          setMsg('⚠ Lost contact with backend — check status manually');
        }
      }
    };

    poll();
    pollRef.current = setInterval(poll, 2000);
  };
  
  const run = async (type) => {
    setRunning(true);
    setRunType(type);
    setMsg('Starting...');
    try {
      const fn     = type === 'crossovers' ? startCrossoverCalculation : startVPatternCalculation;
      const params = { timeframes: '5min,15min,1H,1D' };
      if (symbol.trim())  params.symbols        = symbol.trim().toUpperCase();
      else if (watchlist) params.watchlist_name = watchlist;

      const res = await fn(params);
      if (res.status === 'started') {
        setMsg(`Running PID ${res.pid}...`);
        startPolling(type);
      } else {
        setMsg(`Error: ${res.message}`);
        setRunning(false);
      }
    } catch (e) {
      setMsg('Failed to start — check backend');
      setRunning(false);
    }
  };

  return (
    <div style={styles.calcSection}>
      <select
        style={styles.sel}
        value={watchlist}
        onChange={e => { setWatchlist(e.target.value); setSymbol(''); }}
        disabled={!!symbol.trim() || running}
      >
        <option value="">All Stocks</option>
        {watchlists.map(w => (
          <option key={w.id} value={w.name}>{w.name}</option>
        ))}
      </select>

      <span style={{fontSize:'11px', color:'#999'}}>or</span>
      <input
        style={{...styles.sel, width:'90px'}}
        placeholder="Symbol"
        value={symbol}
        onChange={e => { setSymbol(e.target.value); setWatchlist(''); }}
        disabled={running}
      />

      <button
        style={{...styles.calcBtn, opacity: running ? 0.5 : 1}}
        onClick={() => run('crossovers')}
        disabled={running}
      >
        {running && runType === 'crossovers' ? '⏳ Running...' : '⚙ Crossovers'}
      </button>
      <button
        style={{...styles.calcBtn, opacity: running ? 0.5 : 1}}
        onClick={() => run('vpatterns')}
        disabled={running}
      >
        {running && runType === 'vpatterns' ? '⏳ Running...' : '⚙ V Patterns'}
      </button>

      {msg && (
        <span style={{
          ...styles.calcMsg,
          maxWidth: '400px',
          color: msg.startsWith('✅') ? '#2e7d32' : '#444',
          fontWeight: msg.startsWith('✅') ? 'bold' : 'normal',
        }}>
          {msg}
        </span>
      )}
    </div>
  );
}

// ============================================================
// MAIN COMPONENT
// ============================================================
function EMAReport() {
  const [activeTab,     setActiveTab]     = useState('vpatterns');
  const [filterOptions, setFilterOptions] = useState({ symbols: [], timeframes: [], emas: [] });

  useEffect(() => {
    fetchCrossoverFilters()
      .then(setFilterOptions)
      .catch(console.error);
  }, []);

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div style={styles.tabs}>
          <button
            style={{...styles.tab, ...(activeTab === 'vpatterns' ? styles.tabActive : {})}}
            onClick={() => setActiveTab('vpatterns')}>
            V Patterns
          </button>
          <button
            style={{...styles.tab, ...(activeTab === 'crossovers' ? styles.tabActive : {})}}
            onClick={() => setActiveTab('crossovers')}>
            Crossovers
          </button>
        </div>
        <CalculateSection />
      </div>

      <div style={styles.content}>
        {activeTab === 'crossovers'
          ? <CrossoversTab filterOptions={filterOptions} />
          : <VPatternsTab  filterOptions={filterOptions} />
        }
      </div>
    </div>
  );
}

// ============================================================
// STYLES
// ============================================================
const styles = {
  container: {
    display:       'flex',
    flexDirection: 'column',
    flex:          1,
    overflow:      'hidden',
    background:    '#f5f5f5',
  },
  header: {
    display:         'flex',
    alignItems:      'center',
    justifyContent:  'space-between',
    padding:         '8px 16px',
    backgroundColor: '#fff',
    borderBottom:    '1px solid #e0e0e0',
    flexShrink:      0,
    flexWrap:        'wrap',
    gap:             '8px',
  },
  tabs: {
    display: 'flex',
    gap:     '4px',
  },
  tab: {
    padding:         '5px 16px',
    border:          '1px solid #ddd',
    borderRadius:    '4px',
    background:      '#f5f5f5',
    cursor:          'pointer',
    fontSize:        '13px',
    color:           '#555',
  },
  tabActive: {
    background:   '#2962ff',
    color:        '#fff',
    borderColor:  '#2962ff',
    fontWeight:   'bold',
  },
  calcSection: {
    display:    'flex',
    alignItems: 'center',
    gap:        '8px',
  },
  calcBtn: {
    padding:      '5px 12px',
    background:   '#f0f4ff',
    border:       '1px solid #c5d8fd',
    borderRadius: '4px',
    cursor:       'pointer',
    fontSize:     '12px',
    color:        '#2962ff',
  },
  calcMsg: {
    fontSize: '12px',
    color:    '#666',
  },
  content: {
    flex:      1,
    overflow:  'auto',
    padding:   '8px 16px',
  },
  filterRow: {
    display:      'flex',
    alignItems:   'center',
    gap:          '8px',
    flexWrap:     'wrap',
    marginBottom: '12px',
    padding:      '8px',
    background:   '#fff',
    borderRadius: '6px',
    border:       '1px solid #e0e0e0',
  },
  sel: {
    padding:      '4px 8px',
    border:       '1px solid #ddd',
    borderRadius: '4px',
    fontSize:     '12px',
    color:        '#444',
    background:   '#fff',
  },
  windowRow: {
    display: 'flex',
    gap:     '3px',
  },
  winBtn: {
    padding:      '3px 8px',
    border:       '1px solid #ddd',
    borderRadius: '3px',
    background:   '#f5f5f5',
    cursor:       'pointer',
    fontSize:     '11px',
    color:        '#555',
  },
  winBtnActive: {
    background:  '#2962ff',
    color:       '#fff',
    borderColor: '#2962ff',
  },
  dateInput: {
    padding:      '3px 6px',
    border:       '1px solid #ddd',
    borderRadius: '3px',
    fontSize:     '12px',
  },
  count: {
    fontSize:   '12px',
    color:      '#888',
    marginLeft: 'auto',
  },
  chartsBtn: {
    padding:      '5px 12px',
    background:   '#2962ff',
    color:        '#fff',
    border:       'none',
    borderRadius: '4px',
    cursor:       'pointer',
    fontSize:     '12px',
    fontWeight:   'bold',
  },
  tableWrap: {
    overflowX:       'auto',
    backgroundColor: '#fff',
    borderRadius:    '6px',
    border:          '1px solid #e0e0e0',
  },
  table: {
    width:          '100%',
    borderCollapse: 'collapse',
    fontSize:       '12px',
  },
  th: {
    padding:       '8px 10px',
    borderBottom:  '2px solid #e0e0e0',
    textAlign:     'left',
    color:         '#666',
    fontSize:      '11px',
    fontWeight:    'bold',
    textTransform: 'uppercase',
    whiteSpace:    'nowrap',
    background:    '#fafafa',
  },
  tr: { borderBottom: '1px solid #f5f5f5' },
  td: {
    padding:   '7px 10px',
    color:     '#333',
    whiteSpace:'nowrap',
  },
  empty: {
    padding:   '20px',
    textAlign: 'center',
    color:     '#999',
  },
  filterBlock: {
    background:   '#fff',
    border:       '1px solid #e0e0e0',
    borderRadius: '6px',
    marginBottom: '12px',
    overflow:     'hidden',
  },
  filterHeader: {
    display:    'flex',
    alignItems: 'center',
    padding:    '8px 12px',
    cursor:     'pointer',
    background: '#fafafa',
    borderBottom: '1px solid #e0e0e0',
    userSelect: 'none',
  },
  filterBody: {
    padding: '12px',
  },
  filterGrid: {
    display:             'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
    gap:                 '10px',
  },
  filterGroup: {
    display:       'flex',
    flexDirection: 'column',
    gap:           '4px',
  },
  filterLabel: {
    fontSize:   '11px',
    color:      '#666',
    fontWeight: 'bold',
  },
  numInput: {
    width:        '80px',
    padding:      '4px 8px',
    border:       '1px solid #ddd',
    borderRadius: '4px',
    fontSize:     '12px',
    color:        '#444',
  },
};

export default EMAReport;